<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useId, watch } from 'vue';
import type { FunctionalComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { Check, ChevronDown } from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';

/** Lucide `Languages` — resolved like `@/nav/lucideNavIcons` for TS compatibility. */
const LanguagesIcon = (LucideIcons as Record<string, FunctionalComponent>).Languages;
import { useRoute, useRouter } from 'vue-router';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { usePathVsUiLocaleMismatch } from '@/composables/usePathVsUiLocaleMismatch';
import { applyI18nLocaleForCurrentPath, writePreferredUiLocale } from '@/uiLocalePreference';
import { useContentStore } from '@/stores/content';

const props = withDefaults(
  defineProps<{
    /**
     * `panel`: bordered full-width (legacy); opens dropdown.
     * `drawer`: full-width ghost row (mobile nav); cycles locale on tap like prototype (no dropdown).
     * `toolbar`: compact ghost control (desktop header); opens dropdown.
     */
    variant?: 'panel' | 'toolbar' | 'drawer';
  }>(),
  { variant: 'panel' }
);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const contentStore = useContentStore();
const rootRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const mismatchHintId = useId();
const { isMismatch, contentPageLocale, hintShort, hintTitle } = usePathVsUiLocaleMismatch();

const LANGUAGE_META: Record<SupportedLocale, { label: string; flag: string; shortCode: string }> = {
  tet: { label: 'Tetum', flag: '🇹🇱', shortCode: 'TET' },
  en: { label: 'English', flag: '🇬🇧', shortCode: 'EN' },
  pt: { label: 'Portuguese', flag: '🇵🇹', shortCode: 'PT' },
};

/** Toolbar: compact `(TET)` when Wagtail page locale ≠ UI locale. */
const contentLocaleCodeInParens = computed(() => {
  if (!isMismatch.value || !contentPageLocale.value) return '';
  return `(${LANGUAGE_META[contentPageLocale.value].shortCode})`;
});

const translationLinks = computed(() =>
  contentStore.getTranslationLinks(route.path)
);

const uiLocale = useUiDisplayLocale();

/** Map locale -> translation link (url + title) if the current page has that translation. */
const linkByLocale = computed(() => {
  const map = new Map<string, { url: string; title: string }>();
  for (const link of translationLinks.value) {
    map.set(link.locale, { url: link.url, title: link.title });
  }
  return map;
});

/** Vue Router-only routes (not Wagtail listing): same URL for every UI locale; no CMS translation rows. */
function isLocaleAgnosticAppPath(pathname: string): boolean {
  if (pathname === '/account' || pathname.startsWith('/account/')) return true;
  if (pathname.startsWith('/help-query/')) return true;
  if (pathname.startsWith('/certificate/verify')) return true;
  return false;
}

function closeDropdown() {
  isOpen.value = false;
}

function handleDocumentClick(event: MouseEvent) {
  if (!rootRef.value?.contains(event.target as Node)) {
    closeDropdown();
  }
}

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

/** HorizontalNav mobile: en → tet → pt → en (immediate rotation, no dropdown). */
function nextLocaleInPrototypeCycle(locale: SupportedLocale): SupportedLocale {
  if (locale === 'en') return 'tet';
  if (locale === 'tet') return 'pt';
  return 'en';
}

async function handleDrawerCycle() {
  let target = nextLocaleInPrototypeCycle(uiLocale.value);
  for (let step = 0; step < SUPPORTED_LOCALES.length; step++) {
    const opt = languageOptions.value.find((o) => o.locale === target);
    if (opt?.enabled && opt.url) {
      const navigates = opt.url !== route.fullPath;
      if (!navigates && opt.locale === uiLocale.value) {
        target = nextLocaleInPrototypeCycle(target);
        continue;
      }
      writePreferredUiLocale(opt.locale);
      if (navigates) {
        await router.push(opt.url);
      } else {
        applyI18nLocaleForCurrentPath(route.path);
      }
      return;
    }
    target = nextLocaleInPrototypeCycle(target);
  }
}

function onTriggerClick() {
  if (props.variant === 'drawer') {
    void handleDrawerCycle();
    return;
  }
  toggleDropdown();
}

/** All three language options: locale metadata, url if available, and whether we're on this locale. */
const languageOptions = computed(() => {
  const spaSharedPath = isLocaleAgnosticAppPath(route.path);
  return SUPPORTED_LOCALES.map((locale) => {
    const meta = LANGUAGE_META[locale];
    const translation = linkByLocale.value.get(locale);
    const isCurrent = uiLocale.value === locale;
    const url =
      translation?.url ??
      (spaSharedPath ? route.fullPath : isCurrent ? route.fullPath : null);
    const enabled =
      translation != null || isCurrent || spaSharedPath;
    return {
      locale,
      label: meta.label,
      shortCode: meta.shortCode,
      flag: meta.flag,
      url,
      enabled,
      isCurrent,
    };
  });
});

const currentLanguage = computed(
  () => languageOptions.value.find((option) => option.isCurrent) ?? languageOptions.value[0]
);

async function handleLanguageSelect(
  option: (typeof languageOptions.value)[number]
) {
  if (!option.enabled || !option.url) return;

  closeDropdown();

  writePreferredUiLocale(option.locale);
  if (option.url !== route.fullPath) {
    await router.push(option.url);
  } else {
    applyI18nLocaleForCurrentPath(route.path);
  }
}

watch(() => route.fullPath, closeDropdown);

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleDocumentClick);
});
</script>

<template>
  <div
    ref="rootRef"
    class="language-select"
    :class="{
      'language-select--toolbar': props.variant === 'toolbar',
      'language-select--drawer': props.variant === 'drawer',
    }"
  >
    <span
      v-if="isMismatch"
      :id="mismatchHintId"
      class="language-select__sr-only"
    >{{ hintTitle }}</span>
    <button
      type="button"
      class="language-select__trigger"
      :class="{
        'language-select__trigger--toolbar': props.variant === 'toolbar',
        'language-select__trigger--drawer': props.variant === 'drawer',
      }"
      :aria-expanded="props.variant === 'drawer' ? false : isOpen"
      :aria-haspopup="props.variant === 'drawer' ? false : 'menu'"
      :aria-describedby="isMismatch ? mismatchHintId : undefined"
      :title="isMismatch ? hintTitle : undefined"
      :aria-label="
        props.variant === 'toolbar' || props.variant === 'drawer'
          ? t('nav.changeLanguage')
          : t('nav.selectLanguage')
      "
      @click="onTriggerClick"
    >
      <template v-if="props.variant === 'toolbar'">
        <LanguagesIcon class="language-select__toolbar-icon" aria-hidden="true" />
        <span class="language-select__toolbar-locale-wrap">
          <span class="language-select__code">{{ currentLanguage?.shortCode ?? '' }}</span>
          <span
            v-if="isMismatch"
            class="language-select__content-hint language-select__content-hint--toolbar"
            aria-hidden="true"
          >{{ contentLocaleCodeInParens }}</span>
        </span>
      </template>
      <template v-else-if="props.variant === 'drawer'">
        <span class="language-select__drawer-main">
          <span class="language-select__flag" aria-hidden="true">
            {{ currentLanguage?.flag ?? '' }}
          </span>
          <span class="language-select__drawer-label">
            {{ t('nav.language') }}
            {{ currentLanguage?.shortCode ?? '' }}
            <span
              v-if="isMismatch"
              class="language-select__drawer-hint"
              aria-hidden="true"
            > — {{ hintShort }}</span>
          </span>
        </span>
      </template>
      <template v-else>
        <span class="language-select__flag" aria-hidden="true">
          {{ currentLanguage?.flag ?? '' }}
        </span>
        <span class="language-select__panel-text">
          <span class="language-select__code">{{ currentLanguage?.shortCode ?? '' }}</span>
          <span
            v-if="isMismatch"
            class="language-select__content-hint language-select__content-hint--panel"
            aria-hidden="true"
          >{{ hintShort }}</span>
        </span>
      </template>
      <ChevronDown
        class="language-select__chevron"
        :class="{
          'language-select__chevron--open': isOpen && props.variant !== 'drawer',
          'language-select__chevron--toolbar': props.variant === 'toolbar',
        }"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="isOpen && props.variant !== 'drawer'"
      class="language-select__menu"
      :class="{
        'language-select__menu--toolbar': props.variant === 'toolbar',
      }"
      role="menu"
      :aria-label="t('nav.selectLanguage')"
    >
      <button
        v-for="opt in languageOptions"
        :key="opt.locale"
        type="button"
        class="language-select__option"
        :class="{
          'language-select__option--current': opt.isCurrent,
          'language-select__option--disabled': !opt.enabled,
        }"
        :aria-label="opt.label"
        :aria-current="opt.isCurrent ? 'page' : undefined"
        :disabled="!opt.enabled"
        @click="handleLanguageSelect(opt)"
      >
        <span class="language-select__option-main">
          <span class="language-select__flag" aria-hidden="true">
            {{ opt.flag }}
          </span>
          <span class="language-select__option-code">{{ opt.shortCode }}</span>
        </span>
        <Check
          v-if="opt.isCurrent"
          class="language-select__check"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
.language-select {
  position: relative;
  width: 100%;
}

.language-select__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.language-select__toolbar-locale-wrap {
  display: inline-flex;
  align-items: baseline;
  gap: 0.125rem;
  min-width: 0;
  max-width: 7rem;
}

.language-select__toolbar-locale-wrap .language-select__code {
  flex: 0 0 auto;
}

.language-select__panel-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 0.125rem;
}

.language-select__content-hint {
  font-size: 0.625rem;
  font-weight: 500;
  line-height: 1.2;
  color: var(--side-nav-text-muted, #6b7280);
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.language-select__content-hint--panel {
  font-size: 0.6875rem;
  font-weight: 400;
  white-space: nowrap;
}

/* Match `.language-select__code` in header toolbar (inherit trigger: size, weight, colour, hover). */
.language-select__trigger--toolbar .language-select__content-hint--toolbar {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  letter-spacing: inherit;
}

.language-select__drawer-hint {
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--side-nav-text-muted, #6b7280);
}

.language-select--toolbar {
  width: auto;
  display: inline-block;
}

.language-select__trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  height: 45px;
  padding: 0 12px;
  border: 1px solid var(--side-nav-border, #e5e7eb);
  border-radius: 0.5rem;
  background: var(--side-nav-bg, #fff);
  color: var(--side-nav-text, #1a1a1a);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.language-select__trigger--toolbar {
  width: auto;
  /* Match .side-nav__menu-btn / .side-nav__login-btn (2.5rem) so header rows align */
  min-height: 2.5rem;
  height: 2.5rem;
  padding: 0 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  transition: color 0.2s ease, background 0.2s ease;
}

.language-select__trigger--toolbar:hover {
  color: var(--color-primary, #047857);
  background: color-mix(in srgb, var(--color-primary, #047857) 5%, transparent);
}

.language-select__trigger:not(.language-select__trigger--toolbar):not(
    .language-select__trigger--drawer
  ) {
  min-height: 45px;
  height: auto;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.language-select__trigger:not(.language-select__trigger--toolbar):not(
    .language-select__trigger--drawer
  ):hover {
  background: var(--side-nav-input-bg, #f5f5f5);
}

/* HorizontalNav mobile: w-full px-4 py-3 rounded-lg text-sm font-medium hover:bg-primary/5 hover:text-primary */
.language-select__trigger--drawer {
  justify-content: space-between;
  height: auto;
  min-height: 0;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.language-select__trigger--drawer:hover {
  background: color-mix(in srgb, var(--side-nav-primary, #047857) 5%, transparent);
  color: var(--side-nav-primary, #047857);
}

.language-select__drawer-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.language-select__trigger--drawer .language-select__flag {
  flex-shrink: 0;
}

.language-select__drawer-label {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
}

.language-select__toolbar-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: inherit;
}

.language-select--toolbar .language-select__code {
  flex: 0 0 auto;
}

.language-select__trigger:focus-visible,
.language-select__option:focus-visible {
  outline: 2px solid var(--side-nav-primary, #1f7a5a);
  outline-offset: 2px;
}

.language-select__flag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  line-height: 1;
}

.language-select__code {
  flex: 1;
  text-align: left;
}

.language-select__panel-text .language-select__code {
  flex: 0 0 auto;
}

.language-select__chevron {
  width: 1rem;
  height: 1rem;
  color: var(--side-nav-text-muted, #666);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.language-select__chevron--open {
  transform: rotate(180deg);
}

.language-select__chevron--toolbar {
  width: 0.75rem;
  height: 0.75rem;
}

.language-select__menu {
  position: absolute;
  top: 100%;
  right: 0;
  width: 160px;
  margin-top: 0.5rem;
  border: 1px solid var(--side-nav-border, #e5e7eb);
  border-radius: 12px;
  background: var(--side-nav-bg, #fff);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 60;
}

.language-select__menu--toolbar {
  border-radius: 6px;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
}

.language-select__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--side-nav-text, #1a1a1a);
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
}

.language-select__option:hover {
  background: color-mix(in srgb, var(--side-nav-primary, #047857) 8%, transparent);
  color: var(--side-nav-primary, #047857);
}

.language-select__option--current {
  background: color-mix(in srgb, var(--side-nav-primary, #047857) 10%, transparent);
  color: var(--side-nav-primary, #047857);
}

.language-select__option--current:hover {
  background: color-mix(in srgb, var(--side-nav-primary, #047857) 14%, transparent);
}

.language-select__option-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-select__option-code {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 20px;
}

.language-select__option--disabled {
  opacity: 0.6;
  cursor: default;
}

.language-select__option--disabled:hover {
  background: transparent;
  color: var(--side-nav-text, #1a1a1a);
}

.language-select__check {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: var(--side-nav-primary, #1f7a5a);
}
</style>
